<template>
  <div>
    <!-- Page Header -->
    <div class="mb-6">
      <h1 class="text-h5 font-weight-bold">Generate Labels</h1>
      <p class="text-body-2 grey--text mt-1">Create and print device serial number labels</p>
    </div>

    <v-row>
      <!-- Left Column -->
      <v-col cols="12" md="8">
        <!-- Generation Form -->
        <v-card class="mb-6 elevation-0">
          <v-overlay :value="loading" absolute>
            <v-progress-circular indeterminate size="48" color="primary" />
          </v-overlay>
          <v-card-text class="pa-6">
            <v-form ref="generateForm" @submit.prevent="generateSerials">
              <v-row align="center">
                <v-col cols="12" md="4">
                  <div class="text-caption text-uppercase font-weight-bold mb-2 grey--text">Product</div>
                  <v-combobox
                    v-model="selectedProduct"
                    :items="products"
                    placeholder="Select or type product"
                    outlined
                    dense
                    hide-details
                    :rules="[function(v) { return !!v || 'Required' }]"
                  />
                </v-col>
                <v-col cols="6" md="2">
                  <div class="text-caption text-uppercase font-weight-bold mb-2 grey--text">Quantity</div>
                  <v-text-field
                    v-model.number="count"
                    type="number"
                    outlined
                    dense
                    hide-details
                    min="1"
                    max="1000"
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-caption text-uppercase font-weight-bold mb-2 grey--text">Label Size</div>
                  <v-btn-toggle v-model="labelSizeIndex" mandatory dense class="size-toggle">
                    <v-btn small>12mm</v-btn>
                    <v-btn small>24mm</v-btn>
                  </v-btn-toggle>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="primary"
                    block
                    rounded
                    @click="generateSerials"
                    :loading="loading"
                    class="generate-btn mt-4 mt-md-0"
                  >
                    <v-icon left>mdi-play</v-icon>
                    Generate
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Results -->
        <v-card v-if="generatedSerials.length" class="elevation-0">
          <v-card-title class="px-6 pt-5 pb-2">
            <div>
              <span class="text-subtitle-1 font-weight-bold">Generated</span>
              <v-chip small color="primary" class="ml-2" dark>{{ generatedSerials.length }}</v-chip>
            </div>
            <v-spacer />
            <v-btn
              color="success"
              rounded
              small
              @click="printAll"
              :loading="printingAll"
              :disabled="!printerOnline || allPrinted"
              class="text-none"
            >
              <v-icon left small>mdi-printer</v-icon>
              Print All
            </v-btn>
          </v-card-title>
          <v-data-table
            :headers="serialHeaders"
            :items="generatedSerials"
            :items-per-page="50"
            hide-default-footer
            class="px-2"
          >
            <template v-slot:item.serial="{ item }">
              <span class="font-weight-medium monospace">{{ item.serial }}</span>
            </template>
            <template v-slot:item.printed="{ item }">
              <v-icon v-if="item.printed" small color="success">mdi-check-circle</v-icon>
              <v-icon v-else small color="grey lighten-1">mdi-circle-outline</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" @click="previewLabel(item)" class="mr-1">
                    <v-icon>mdi-eye-outline</v-icon>
                  </v-btn>
                </template>
                <span>Preview</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" :href="downloadUrl(item.serial)" target="_blank" class="mr-1">
                    <v-icon>mdi-download-outline</v-icon>
                  </v-btn>
                </template>
                <span>Download PDF</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    v-on="on"
                    @click="printOne(item)"
                    :disabled="!printerOnline || item.printed"
                    :loading="item.printing"
                  >
                    <v-icon>mdi-printer-outline</v-icon>
                  </v-btn>
                </template>
                <span>Print</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>

        <!-- Empty State -->
        <v-card v-else class="elevation-0">
          <v-card-text class="text-center pa-12">
            <v-icon size="64" color="grey lighten-1">mdi-label-multiple-outline</v-icon>
            <div class="text-h6 grey--text mt-4">No labels generated yet</div>
            <div class="text-body-2 grey--text text--lighten-1 mt-1">Select a product and click Generate to start</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Column -->
      <v-col cols="12" md="4">
        <PrintPanel ref="printPanel" @status-change="onPrinterStatus" />

        <!-- Quick Stats -->
        <v-card v-if="generatedSerials.length" class="elevation-0 mt-4">
          <v-card-text class="pa-5">
            <div class="text-caption text-uppercase font-weight-bold grey--text mb-3">Session Stats</div>
            <div class="d-flex justify-space-between mb-3">
              <span class="text-body-2 grey--text">Generated</span>
              <span class="font-weight-bold">{{ generatedSerials.length }}</span>
            </div>
            <div class="d-flex justify-space-between mb-3">
              <span class="text-body-2 grey--text">Printed</span>
              <span class="font-weight-bold success--text">{{ printedCount }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span class="text-body-2 grey--text">Pending</span>
              <span class="font-weight-bold warning--text">{{ generatedSerials.length - printedCount }}</span>
            </div>
            <v-progress-linear
              :value="(printedCount / generatedSerials.length) * 100"
              color="success"
              rounded
              height="6"
              class="mt-4"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <LabelPreview v-model="previewDialog" :serial="previewSerial" :label-size="labelSize" />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" bottom right rounded="pill">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import api from '../api';
import PrintPanel from './PrintPanel.vue';
import LabelPreview from './LabelPreview.vue';

var API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8001';

export default {
  name: 'LabelGenerator',
  components: { PrintPanel: PrintPanel, LabelPreview: LabelPreview },
  data: function () {
    return {
      selectedProduct: null,
      count: 1,
      labelSizeIndex: 0,
      loading: false,
      generatedSerials: [],
      printerOnline: false,
      printingAll: false,
      previewDialog: false,
      previewSerial: null,
      snackbar: { show: false, text: '', color: 'success' },
      serialHeaders: [
        { text: 'Serial Number', value: 'serial', sortable: false },
        { text: 'Product', value: 'product_code', sortable: false },
        { text: 'Printed', value: 'printed', sortable: false, align: 'center', width: 80 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 150 },
      ],
    };
  },
  computed: {
    products: function () {
      return this.$store.state.products;
    },
    labelSize: function () {
      return this.labelSizeIndex === 0 ? '12mm' : '24mm';
    },
    printedCount: function () {
      return this.generatedSerials.filter(function (s) { return s.printed; }).length;
    },
    allPrinted: function () {
      return this.printedCount === this.generatedSerials.length;
    },
  },
  methods: {
    generateSerials: function () {
      if (!this.$refs.generateForm.validate()) return;
      var self = this;
      self.loading = true;
      api
        .post('/serial/generate', {
          product_code: self.selectedProduct,
          count: self.count,
        })
        .then(function (res) {
          self.generatedSerials = res.data.serials.map(function (s) {
            return Object.assign({}, s, { printed: false, printing: false });
          });
          self.notify('Generated ' + self.generatedSerials.length + ' serials', 'success');
        })
        .catch(function (err) {
          self.notify((err.response && err.response.data && err.response.data.detail) || 'Generation failed', 'error');
        })
        .finally(function () {
          self.loading = false;
        });
    },
    downloadUrl: function (serial) {
      return API_URL + '/api/labelbase/label/download/' + serial + '?label_size=' + this.labelSize;
    },
    previewLabel: function (item) {
      this.previewSerial = item.serial;
      this.previewDialog = true;
    },
    printOne: function (item) {
      var self = this;
      item.printing = true;
      var imgUrl = API_URL + '/api/labelbase/label/image/' + item.serial + '?label_size=' + self.labelSize;
      return fetch(imgUrl)
        .then(function (resp) {
          if (!resp.ok) throw new Error('Failed to fetch label image');
          return resp.blob();
        })
        .then(function (blob) {
          var form = new FormData();
          form.append('file', blob, item.serial + '.png');
          form.append('size', self.labelSize);
          return fetch('http://localhost:8081/serial/print-image', { method: 'POST', body: form });
        })
        .then(function (printResp) {
          if (!printResp.ok) throw new Error('Print failed');
          return api.post('/label/print', { serial: item.serial, label_size: self.labelSize });
        })
        .then(function () {
          item.printed = true;
          self.notify('Printed ' + item.serial, 'success');
        })
        .catch(function (err) {
          self.notify(err.message || 'Print failed', 'error');
        })
        .finally(function () {
          item.printing = false;
        });
    },
    printAll: function () {
      var self = this;
      self.printingAll = true;
      var unprinted = self.generatedSerials.filter(function (s) { return !s.printed; });
      var chain = Promise.resolve();
      unprinted.forEach(function (item) {
        chain = chain.then(function () { return self.printOne(item); });
      });
      chain.finally(function () { self.printingAll = false; });
    },
    onPrinterStatus: function (online) {
      this.printerOnline = online;
    },
    notify: function (text, color) {
      this.snackbar = { show: true, text: text, color: color };
    },
  },
};
</script>

<style scoped>
.monospace { font-family: 'Roboto Mono', monospace; font-size: 13px; }
.generate-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
}
.size-toggle {
  border-radius: 8px !important;
}
.size-toggle .v-btn {
  text-transform: none !important;
}
</style>
